'use client'

import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { endsWith } from 'lodash'
import { isAddress } from 'viem'
import { normalize } from 'viem/ens'
import { useEnsAvatar as wagmiUseEnsAvatar } from 'wagmi'
import { mainnet } from 'wagmi/chains'

import { getChain, SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'
import { ensPublicClient } from '~/utils/ens'
import { loadEnsName } from '~/utils/ensBatcher'
import { captureException } from '~/utils/errors'
import { getReadPublicClient } from '~/utils/requests'

export const hasENSDomain: (arg0: string) => boolean = (address) =>
  ['.eth', '.xyz', '.luxe', '.kred', '.art', '.club'].some((tld) =>
    endsWith(address, tld),
  )

const useLookupAddress: () => (
  address: IAddress,
  chainId: SupportedChainId,
) => Promise<string | null | undefined> = () => {
  return useCallback(async (address: IAddress) => {
    try {
      // Reverse resolution is mainnet-based; the batcher coalesces concurrent
      // addresses into one reverseRecords.getNames multicall.
      const ens = await loadEnsName({ address, chainId: mainnet.id })
      return ens ?? null
    } catch (e) {
      captureException(e)
      return null
    }
  }, [])
}

export const useLookupENS: (
  toastError?: boolean,
) => (
  ens: string,
  chainId: SupportedChainId,
) => Promise<IAddress | undefined> = () => {
  const queryClient = useQueryClient()

  const lookupENS = useCallback(
    async (ens: string, chainId: SupportedChainId) => {
      const publicClient = getReadPublicClient(chainId)

      if (publicClient && ensPublicClient && chainId)
        try {
          let normalizedEns
          try {
            normalizedEns = normalize(ens)
          } catch {
            // If the normalization fails, the ENS is invalid
            return
          }

          // Step 1: chain forward resolution
          const chain = getChain(chainId)
          if (chain?.supportsEns) {
            const coinType = BigInt(chain.ensCoinType ?? 0)
            const address = await ensPublicClient.getEnsAddress({
              name: normalizedEns,
              coinType: BigInt(coinType),
            })
            if (address) return EvmAddress.format(address)
            if (chainId === mainnet.id) return
          }

          // Step 2: mainnet forward resolution
          const mainnetForwardAddress = await ensPublicClient.getEnsAddress({
            name: normalizedEns,
          })
          if (!mainnetForwardAddress) return

          // Step 3a: mainnet smart contract check
          const [mainnetBytecode, chainBytecode] = await Promise.all([
            ensPublicClient.getBytecode({ address: mainnetForwardAddress }),
            publicClient.getBytecode({ address: mainnetForwardAddress }),
          ])
          if (mainnetBytecode || chainBytecode) return

          return EvmAddress.format(mainnetForwardAddress)
        } catch (e) {
          captureException(e)
        }
    },
    [],
  )

  // Wrap lookupENS to use react-query cache
  return useCallback(
    async (ens: string, chainId: SupportedChainId) => {
      const queryKey = [QUERY_KEYS.ENS_LOOKUP, chainId, ens]
      const cachedData = queryClient.getQueryData<string>(queryKey)
      if (cachedData && isAddress(cachedData)) {
        return cachedData as IAddress
      }
      const result = await lookupENS(ens, chainId)
      if (result) {
        queryClient.setQueryData(queryKey, result)
      }
      return result
    },
    [lookupENS, queryClient],
  )
}

export const useEnsAvatar = ({ name }: { name?: string }) => {
  const {
    addressSearchHistory: { searchHistory },
  } = useAuth()
  const query = wagmiUseEnsAvatar({
    name: name ? normalize(name) : undefined,
    query: {
      initialData: () => {
        return searchHistory.find(
          (result) => result.ens && result.ens.name === name,
        )?.ens?.avatar
      },
      retry: false,
      retryOnMount: false,
      enabled: !!name,
      staleTime: Infinity, // Considered fresh indefinitely
    },
  })

  return {
    avatar: query.data ?? undefined,
  }
}

export const useENSLookup = (
  ens: string,
  chainId: SupportedChainId = mainnet.id,
) => {
  const lookupENS = useLookupENS()
  const queryClient = useQueryClient()
  const {
    addressSearchHistory: { searchHistory },
  } = useAuth()

  const query = useQuery({
    queryKey: [QUERY_KEYS.ENS_LOOKUP, chainId, ens],
    queryFn: async () => {
      // ignore if the user used an @ symbol
      if (ens.startsWith('@')) return null

      const address = await lookupENS(ens, chainId)
      if (!address) throw new Error('No ENS result found')

      queryClient.setQueryData(
        [QUERY_KEYS.ADDRESS_LOOKUP, chainId, address],
        ens,
      )

      return address
    },
    initialData: () => {
      return searchHistory.find(
        (result) => result.ens && result.ens.name === ens,
      )?.address
    },
    retry: false,
    retryOnMount: false,
    enabled: !!ens && hasENSDomain(ens),
    staleTime: Infinity, // Considered fresh indefinitely
  })

  return {
    address: query.data ? EvmAddress.format(query.data) : undefined,
    isLoading: query.isLoading,
    // queryFn throws when the name resolves to nothing, so isError means "no ENS"
    notFound: query.isError,
  }
}

export const useAddressLookup = (
  address?: string,
  chainId: SupportedChainId = mainnet.id,
) => {
  const lookupAddress = useLookupAddress()
  const {
    addressSearchHistory: { searchHistory },
  } = useAuth()

  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [QUERY_KEYS.ADDRESS_LOOKUP, chainId, address],
    queryFn: async () => {
      const ens = await lookupAddress(address as IAddress, chainId)
      if (!ens) throw new Error('No address found')

      queryClient.setQueryData(
        [QUERY_KEYS.ADDRESS_LOOKUP, chainId, ens],
        address,
      )
      return ens
    },
    initialData: () => {
      return searchHistory.find(
        (result) => result.ens && result.address === address,
      )?.ens?.name
    },
    retry: false,
    retryOnMount: false,
    enabled: !!address && isAddress(address),
    staleTime: Infinity, // Considered fresh indefinitely
  })
}
